import { type ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { HelpTip } from '@/components/ui/help-tip';
import type { CollectorSummary, DailyEarning } from '../types';
import { weekStates, faultsSoFar, type WeekDay } from '../utils/status-week';
import {
  STRIKES_HELP,
  DAY_LABELS,
  DAY_CLS,
  statusWeekOpts,
  pickTone,
  dayContent,
  type StrikesTone,
} from '../utils/strikes-display';

function StrikesRow({ days }: { days: WeekDay[] }): ReactNode {
  return (
    <div className="mt-sm flex items-center gap-1">
      {days.map((d, i) => (
        <span
          key={d.iso}
          title={d.state === 'omitted' ? 'Día diferido' : undefined}
          className={`flex h-6 flex-1 items-center justify-center rounded text-[10px] font-semibold ${DAY_CLS[d.state]}`}
        >
          {dayContent(d.state, DAY_LABELS[i] as string)}
        </span>
      ))}
    </div>
  );
}

function StrikesHeader({ tone }: { tone: StrikesTone }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-sm">
      <div className="flex items-center gap-sm">
        <ShieldCheck size={14} className="text-text-tertiary/60" />
        <span className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary/60">
          Estado
          <span className="ml-0.5 inline-block normal-case">
            <HelpTip text={STRIKES_HELP} />
          </span>
        </span>
      </div>
      <span className={`text-[11px] font-semibold ${tone.cls}`}>
        {tone.label}
      </span>
    </div>
  );
}

export function StatusStrikes({
  summary,
  daily,
}: {
  summary: CollectorSummary;
  daily: DailyEarning[] | undefined;
}): ReactNode {
  const { user } = useAuth();
  const threshold = summary.daily_minimum_leads ?? 33;
  const days = weekStates(daily, threshold, {
    ...statusWeekOpts(user),
    omittedDates: summary.omitted_week_days,
  });
  const tone = pickTone({
    status: user?.account_status ?? '',
    faults: faultsSoFar(days),
  });
  return (
    <section>
      <StrikesHeader tone={tone} />
      <StrikesRow days={days} />
    </section>
  );
}
